import { useEffect, useMemo, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card, Chip, DataTable, EmptyState, Pane, PaneHead, ReasonTable,
  type TableRow,
} from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { notificationsApi } from '@/services/api/notificationsApi';
import { shortName, since } from '../lib/scope';
import { useNarrowViewport } from '@/components/kit/useNarrow';
import {
  useAlerts, useUnsignedNotes, useWallet, walletRunwayWeeks, type AlertRow,
} from '../lib/useManagerData';
import { DENY_REASONS, type ApprovalRow } from './denyReasons';
import { AutoTopUpModal } from './AutoTopUpModal';
import { PayoutRunDetail, usePayoutRun } from './PayoutRunDetail';
import { AlertFamiliesModal } from './AlertFamiliesModal';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import apiClient from '@/services/apiClient';
import { toast } from 'sonner';

/** One thing waiting on a decision, as `GET /approvals` returns it. */

/**
 * The eight families the board names, in its order, with what each one watches.
 * The list is the SERVER's — `GET /clinic-settings/alert-families` returns it
 * beside the state — so this is only the fallback shape and the short names the
 * page's closing line reads.
 */
const FAMILY_SHORT = [
  'end-of-day digest', 'unpaid at close', 'unsigned > 48 h', 'money edits',
  'insurance', 'schedule', 'stock', 'wallet',
] as const;

/** "S. Adel" for a full name, so a card reads like the board's "requested by S. Adel". */
function initialed(full: string): string {
  const [first, ...rest] = full.trim().split(/\s+/);
  return rest.length ? `${first[0]}. ${rest[rest.length - 1]}` : first;
}

/** Who sent it, in the words the board uses: the accountant prepares a run, anyone else requests. */
function askedBy(r: ApprovalRow): string {
  const who = r.requestedByName ? initialed(r.requestedByName) : '';
  if (r.kind === 'payout_run') return `prepared by the accountant${who ? ` · ${who}` : ''}`;
  return who ? `requested by ${who}` : 'requested from the desk';
}

/**
 * Clinic manager · Alerts & approvals — `g_alerts.dc.html`.
 *
 * EVERYTHING THAT WAITS ON THE MANAGER LANDS HERE. The board lists six kinds:
 * payout runs, duplicate merges, locked-day reopens, provider verifications,
 * proposed Directory edits and the wallet runway.
 *
 * WHAT IS REAL AND WHAT IS NOT, stated rather than mimed:
 *
 *  - REAL: notifications addressed to this manager (the backend's
 *    `requiresAcknowledgment` flag is exactly the board's "waiting on you"),
 *    acknowledging one, the clinic's unsigned backlog, and the message wallet's
 *    runway from `/me/wallet`.
 *  - NOT BUILT: an APPROVAL as an object — a payout run, a merge request, a
 *    reopen request or a proposed Directory edit that a manager approves or
 *    denies with a reason. There is no approvals table, no state machine and no
 *    endpoint; the Directory lane owns the provider-verification half of it.
 *    So this screen shows what genuinely waits and says plainly that the
 *    approve/deny queue is not built, instead of drawing six rows of fiction
 *    with live-looking buttons on them.
 */
export function ManagerAlertsPage() {
  const go = useNavigate();
  const narrow = useNarrowViewport();
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [familiesOpen, setFamiliesOpen] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);

  /* THE DIGEST'S FOOT BUTTON SAYS "Open Alerts ▸ Families", so arriving with
     `?families=1` has to OPEN the sheet — a label that names a surface and
     lands you beside it is the dead control §8 forbids. */
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get('families') !== '1') return;
    setFamiliesOpen(true);
    const next = new URLSearchParams(params);
    next.delete('families');
    setParams(next, { replace: true });
  }, [params, setParams]);

  const alerts = useAlerts();

  /* WHAT IS WAITING ON A DECISION. Separate from the notifications: a
     notification is a thing that happened, a request is somebody blocked. */
  const approvals = useQuery({
    queryKey: ['cm-approvals'],
    staleTime: 30_000,
    /* A request that failed to load must not vanish from the top of the list:
       the two surfaces would disagree about whether somebody is blocked. */
    retry: 2,
    queryFn: async (): Promise<ApprovalRow[]> => {
      const res = await apiClient.get('/approvals');
      return (res.data?.data ?? res.data ?? []) as ApprovalRow[];
    },
  });

  const [denying, setDenying] = useState<ApprovalRow | null>(null);

  const decide = useMutation({
    mutationFn: async (
      input: { id: string; verb: 'approve' } | { id: string; verb: 'deny'; reason: string; note: string },
    ) => {
      if (input.verb === 'approve') {
        await apiClient.post(`/approvals/${input.id}/approve`);
      } else {
        await apiClient.post(`/approvals/${input.id}/deny`, {
          reason: input.reason,
          note: input.note || undefined,
        });
      }
    },
    onSuccess: (_d, input) => {
      toast.success(
        input.verb === 'approve'
          ? 'Approved — it is settled.'
          : 'Returned with your reason. It has not disappeared.',
      );
      setDenying(null);
      void qc.invalidateQueries({ queryKey: ['cm-approvals'] });
    },
    onError: () => toast.error('That was refused. Nothing has changed.'),
  });
  const unsigned = useUnsignedNotes();
  const { wallet } = useWallet();

  usePageMeta({ title: 'Alerts & approvals' });

  const acknowledge = useMutation({
    mutationFn: (ids: string[]) =>
      Promise.all(ids.map((id) => notificationsApi.acknowledgeNotification(id))),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['cm-alerts'] }); },
  });
  const markRead = useMutation({
    mutationFn: (ids: string[]) =>
      Promise.all(ids.map((id) => notificationsApi.markAsRead(id))),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['cm-alerts'] }); },
  });

  interface Waiting {
    id: string;
    title: string;
    from: string;
    when: string;
    sortKey: string;
    action: 'acknowledge' | 'read' | 'open' | 'auto-topup' | 'decide';
    actionLabel: string;
    to?: string;
    body: string;
    /** Every notification this row stands for: one event reaches several logins. */
    ids?: string[];
    /** The one line under the title on a phone card. */
    sub: string;
    tint?: 'warn';
    source: AlertRow | null;
    /** Present only on a row that is a REQUEST — something to approve or return. */
    request?: ApprovalRow;
  }

  const waiting: Waiting[] = useMemo(() => {
    /* WHAT ACTUALLY WAITS ON A DECISION GOES FIRST. A payout run and a day
       reopen are requests — somebody is blocked until this manager answers —
       where a notification is a thing that happened. The board puts them at
       the top of the list for that reason, not for recency. */
    const requests: Waiting[] = (approvals.data ?? []).map((r) => ({
      id: `__req:${r.id}`,
      title: r.title,
      from: askedBy(r),
      sub: [r.note ? `“${r.note}”` : '', askedBy(r)].filter(Boolean).join(' · '),
      when: r.createdAt ? since(r.createdAt) : 'now',
      sortKey: '',
      action: 'decide',
      actionLabel: r.kind === 'payout_run' ? 'Approve & settle' : 'Approve',
      body: r.note || 'No reason was written with it.',
      source: null,
      request: r,
    }));

    /* ONE EVENT IS ONE ROW. A machine marked down is sent to every desk and
       technician login, and the manager can read all of them — five identical
       rows for one machine. Open notifications with the same words minted in
       the same minute are one row, and answering it answers them all. */
    const grouped = new Map<string, AlertRow[]>();
    for (const a of alerts.page.rows) {
      if (a.acknowledgedAt || !(a.requiresAcknowledgment || !a.readAt)) continue;
      const key = `${a.title}|${a.message}|${(a.createdAt || '').slice(0, 16)}`;
      grouped.set(key, [...(grouped.get(key) ?? []), a]);
    }
    const out: Waiting[] = [...grouped.values()].map((rows) => {
      const a = rows[0];
      const many = rows.length > 1;
      const needsAck = rows.some((r) => r.requiresAcknowledgment);
      return {
        id: a.id,
        title: a.title || a.message || 'Notification',
        from: many
          ? 'the clinic'
          : a.user ? `${shortName(a.user)} · ${a.user.role?.name ?? 'staff'}` : 'MAIE',
        when: a.createdAt ? since(a.createdAt) : '—',
        sortKey: a.createdAt || '',
        action: needsAck ? 'acknowledge' : 'read',
        actionLabel: needsAck ? 'Acknowledge' : 'Mark read',
        body: a.message || '',
        sub: a.message || '',
        ids: rows.map((r) => r.id),
        source: a,
      } satisfies Waiting;
    });

    /* NEWEST FIRST among the real notifications — and the two synthetic rows
       are APPENDED, never sorted in. The board puts the wallet LAST, because a
       runway is a standing condition rather than something that arrived; with
       sort keys of "9999"/"9998" they led the queue ahead of everything that
       actually happened today. */
    out.sort((a, b) => (a.sortKey < b.sortKey ? 1 : -1));

    /* THE UNSIGNED BACKLOG — the board's "unsigned > 48 h" family, and the one
       waiting item that is not a notification. */
    if (unsigned.page.total > 0) {
      out.push({
        id: '__unsigned',
        title: `Unsigned notes — ${unsigned.page.total} waiting on their doctors`,
        from: 'MAIE · the clinic’s backlog',
        when: 'now',
        sortKey: '',
        action: 'open',
        actionLabel: 'See the doctors ›',
        to: '/clinic-manager/reports/visits',
        body: 'A note is powerless until it is signed. The manager sees the count, never the note.',
        sub: 'the manager sees the count, never the note',
        source: null,
      });
    }

    /* THE WALLET RUNWAY — the board's last row, and a RUNWAY: "5 weeks left",
       not a balance. `walletRunwayWeeks` reads the burn out of the ledger
       `/me/wallet` returns; when the wallet has never been funded or never
       spent there is no rate to divide by, and the row says which of the two
       it is rather than printing a balance where a runway belongs. */
    /* IT IS ONLY AN ALERT WHEN IT WANTS A DECISION. A funded wallet with auto
       top-up on is the day working, and pushing it here meant this inbox could
       never be empty — so f2c, the authored "Nothing is waiting on you", was
       unreachable. The row appears when the wallet is EMPTY or RUNNING DOWN
       (four weeks or less). Auto top-up being off is a setting, not a decision
       waiting: a clinic that tops up by hand would otherwise carry a permanent
       alert it can never clear. */
    const walletWeeks = wallet ? walletRunwayWeeks(wallet) : null;
    const walletFunded = (wallet?.balanceMinor ?? 0) > 0;
    const walletNeedsYou =
      !!wallet && (!walletFunded || (walletWeeks !== null && walletWeeks <= 4));
    if (wallet && walletNeedsYou) {
      const weeks = walletWeeks;
      const funded = walletFunded;
      out.push({
        id: '__wallet',
        title: weeks !== null
          ? `Message wallet — ${weeks} week${weeks === 1 ? '' : 's'} left`
          : funded
            ? 'Message wallet — funded, nothing spent yet'
            : 'Message wallet — not funded yet',
        from: 'MAIE',
        when: 'now',
        sortKey: '',
        action: 'auto-topup',
        actionLabel: 'Auto top-up ›',
        body: funded
          ? (wallet.autoReload.enabled
            ? 'Auto top-up is on — the wallet reloads itself before it runs dry.'
            : 'Auto top-up is off. When the wallet empties, reminders and messages stop going out.')
          : 'Nothing has been put in the wallet yet, so reminders and messages have nothing to spend.',
        sub: funded
          ? `auto top-up is ${wallet.autoReload.enabled ? 'on' : 'OFF'}`
          : 'nothing in it yet',
        tint: 'warn',
        source: null,
      });
    }

    return [...requests, ...out];
  }, [alerts.page.rows, unsigned.page.total, wallet, approvals.data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return waiting;
    return waiting.filter((w) => `${w.title} ${w.from} ${w.body}`.toLowerCase().includes(q));
  }, [waiting, query]);

  const selected = selectedId ? filtered.find((w) => w.id === selectedId) ?? null : null;
  const payoutRun = usePayoutRun(selected?.request?.kind === 'payout_run' ? selected.request.subjectRef : null);

  const act = (w: Waiting) => {
    if (w.action === 'decide' && w.request) {
      decide.mutate({ id: w.request.id, verb: 'approve' });
      return;
    }
    if (w.action === 'acknowledge') { acknowledge.mutate(w.ids ?? [w.id]); return; }
    if (w.action === 'read') { markRead.mutate(w.ids ?? [w.id]); return; }
    if (w.action === 'auto-topup') { setTopUpOpen(true); return; }
    if (w.to) go(w.to);
  };

  const rows: TableRow[] = filtered.map((w) => ({
    id: w.id,
    cells: {
      what: <div><p className="t-strong">{w.title}</p></div>,
      from: <span className="t-sec mut">{w.from}</span>,
      when: <span className="t-sec mut">{w.when}</span>,
      /* The whole row is the hit box (§13.1) and the chip sits inside it, so
         pressing "Acknowledge" would ALSO select the row and re-dock the pane
         under the person's hand. The chip's click stops there. The kit's Chip
         takes a handler with no event, so the guard is the wrapper's. */
      act: (
        <span
          role="presentation"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Chip kind={w.action === 'open' || w.action === 'auto-topup' ? 'outline' : 'success'} small
            className={w.action === 'open' || w.action === 'auto-topup' ? undefined : 'w6'}
            onClick={() => act(w)}>
            {w.actionLabel}
          </Chip>
          {/* A REQUEST HAS TWO ANSWERS, and the board draws both on the row.
              Deny opens the reason table — a return with no reason is the
              thing this whole verb exists to prevent. */}
          {w.action === 'decide' && w.request ? (
            <Chip kind="outline" small onClick={() => setDenying(w.request!)}>
              Deny
            </Chip>
          ) : null}
        </span>
      ),
    },
  }));

  const needsYou = filtered.filter((w) => w.action === 'decide' || w.action === 'auto-topup');
  const others = filtered.filter((w) => w.action !== 'decide' && w.action !== 'auto-topup');

  /* THE PHONE'S CARD — the board's: title, one sub-line, the verbs under it,
     the border and fill saying what kind of thing it is. */
  const renderCard = (w: Waiting) => {
    const isRun = w.request?.kind === 'payout_run';
    const style = w.tint === 'warn'
      ? { borderColor: 'var(--warning-600)', background: 'var(--warning-100)' }
      : isRun
        ? { borderColor: 'var(--success-600)' }
        : w.request || w.action === 'auto-topup'
          ? { borderColor: 'var(--brand-300)' }
          : undefined;
    return (
      <div key={w.id} className="card" style={style}>
        <button type="button" className="text-start w-full" style={{ background: 'none', border: 0, padding: 0, color: 'inherit' }}
          onClick={() => setSelectedId(w.id)}>
          <p className="t-strong">{w.title}</p>
          <p className="t-sec mut mt4">{w.sub || w.from}</p>
        </button>
        <div className="chiprow" style={{ marginTop: 10 }}>
          <Chip kind={w.action === 'open' || w.action === 'auto-topup' ? 'outline' : 'success'} small
            className={w.action === 'open' || w.action === 'auto-topup' ? undefined : 'w6'}
            onClick={() => act(w)}>
            {w.actionLabel}
          </Chip>
          {isRun ? <Chip kind="outline" small onClick={() => setSelectedId(w.id)}>Open run</Chip> : null}
          {w.request ? <Chip kind="outline" small onClick={() => setDenying(w.request!)}>Deny</Chip> : null}
        </div>
      </div>
    );
  };

  const paneSig = `${selected?.id ?? ''}:${acknowledge.isPending}:${payoutRun?.netMinor ?? ''}`;

  const crumb = usePhoneCrumb('Home', 'Alerts & approvals');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!selected,
      onPaneClose: () => setSelectedId(null),
      phoneMenu: (close) => <ManagerMenu door="alerts" onClose={close} />,
      title: 'Alerts & approvals',
      sub: `${filtered.length} waiting on you · families: all on`,
      search: 'Search an approval, a person…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: { label: 'Families ▾', onClick: () => setFamiliesOpen(true) },
      pane: selected ? (
        <Pane
          head={<PaneHead overline="The selected alert" title={selected.title} sub={selected.from} />}
          foot={
            selected.request ? (
              /* THE BOARD PUTS BOTH VERBS IN THE FOOT of a request, because
                 both are the decision — not one act and one escape. */
              <>
                <button type="button" className="btn btn-outline" style={{ flex: 1, minHeight: 44 }}
                  disabled={decide.isPending}
                  onClick={() => setDenying(selected.request!)}>
                  Deny &amp; return
                </button>
                <button type="button" className="btn btn-primary" style={{ flex: 1, minHeight: 44 }}
                  disabled={decide.isPending}
                  onClick={() => act(selected)}>
                  {decide.isPending ? 'Working…' : payoutRun ? `Approve all — ${Math.round(payoutRun.netMinor / 100).toLocaleString()}` : 'Approve'}
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-primary" style={{ flex: 1 }}
                disabled={acknowledge.isPending || markRead.isPending}
                onClick={() => act(selected)}>
                {selected.actionLabel}
              </button>
            )
          }
        >
          {selected.request?.kind === 'payout_run' && payoutRun ? (
            <PayoutRunDetail period={selected.request.subjectRef} requestedBy={selected.request.requestedByName} />
          ) : (
            <>
              <Card>
                <p className="t-ovl mut">What it says</p>
                <p className="t-sec sec" style={{ marginTop: 4 }}>{selected.body || 'No detail was sent with it.'}</p>
              </Card>
              <Card>
                <p className="t-ovl mut">From · when</p>
                <p className="t-sec sec" style={{ marginTop: 4 }}>{selected.from} · {selected.when}</p>
              </Card>
            </>
          )}
          <Card kind="accent">
            <p className="t-sec brand">
              {selected.request
                ? 'Approving settles it. Denying returns it with your reason attached — it never disappears.'
                : 'Acknowledging is logged against your account.'}
            </p>
          </Card>
        </Pane>
      ) : !filtered.length && !query.trim() ? (
        <Pane head={<PaneHead overline="The selected alert" title="Nothing is selected" sub="because nothing is waiting" />}>
          <div className="emptystate">
            <p className="t-sec">Nothing is selected, because nothing is waiting.</p>
          </div>
        </Pane>
      ) : undefined,
    },
    [query, paneSig, filtered.length, unsigned.page.total, wallet?.balanceMinor, decide.isPending, selectedId],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        {narrow && filtered.length ? (
          <>
            {needsYou.length ? <p className="t-sec mut mb8">Needs your approval · {needsYou.length}</p> : null}
            {needsYou.map((w) => renderCard(w))}
            {others.length ? <p className="t-sec mut" style={{ margin: '16px 0 8px' }}>Alerts</p> : null}
            {others.map((w) => renderCard(w))}
          </>
        ) : (
        <DataTable
          caption="Waiting on you"
          columns={[
            { key: 'what', label: 'What is waiting', width: 46 },
            { key: 'from', label: 'From', width: 18 },
            { key: 'when', label: 'When', width: 12 },
            { key: 'act', label: '', width: 24 },
          ]}
          rows={rows}
          selectedId={selectedId}
          onSelect={(r) => setSelectedId((cur) => (cur === r.id ? null : r.id))}
          empty={
            alerts.isLoading || approvals.isLoading ? <EmptyState title="Loading what is waiting…" /> : <EmptyState
              title="Nothing is waiting on you"
              /* f2c's own line: the empty inbox has to say it is empty ON
                 PURPOSE, or a manager reads it as a screen that failed to
                 load and goes looking for the work somewhere else. */
              sub="Families are still on — an empty inbox is a quiet week, not a switched-off one. Everything that happened is still in the audit log."
            />
          }
        />
        )}

        {approvals.isError ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
            <span className="t-sec" style={{ color: "var(--danger-600)" }}>Requests waiting on your decision could not be loaded.</span>
            <Chip kind="outline" small onClick={() => void approvals.refetch()}>Try again</Chip>
          </div>
        ) : null}

        {!filtered.length && !query.trim() && !alerts.isLoading && !approvals.isLoading ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Chip kind="outline" small onClick={() => go('/clinic-manager/audit')}>Open the audit log</Chip>
            <Chip kind="outline" small onClick={() => setFamiliesOpen(true)}>Alert families ▾</Chip>
          </div>
        ) : null}

        <p className="t-sec mut" style={{ marginTop: 12 }}>
          Families: {FAMILY_SHORT.join(' · ')}. Acknowledging is one click, and it is logged.
        </p>
      </div>

      <AlertFamiliesModal open={familiesOpen} onClose={() => setFamiliesOpen(false)} />

      {/* `g_m_deny` for a run, `g_l16d` for a reopen — the same picker, the
          table its board draws. Nothing is deleted: it goes back to the person
          who sent it with this line attached. */}
      <ReasonTable
        open={!!denying}
        onClose={() => setDenying(null)}
        title={denying?.kind === 'day_reopen' ? 'Deny the request' : 'Deny & return'}
        sub={denying?.kind === 'day_reopen'
          ? 'Nothing closes silently: the person who asked is told, and your reason travels with it.'
          : 'Nothing is deleted. It goes back to the person who sent it with your line attached, and the log keeps both halves.'}
        reasons={denying ? DENY_REASONS[denying.kind] : []}
        notePlaceholder={denying?.kind === 'day_reopen'
          ? 'One line, in your words'
          : 'the doctors’ subtotal is 2,600 short of what I approved'}
        confirmLabel={denying?.kind === 'day_reopen' ? 'Deny — send it back' : 'Deny & return'}
        busy={decide.isPending}
        onConfirm={(reason, note) =>
          denying && decide.mutate({ id: denying.id, verb: 'deny', reason: reason.key, note })
        }
      />

      <AutoTopUpModal open={topUpOpen} onClose={() => setTopUpOpen(false)} />
    </div>
  );
}
